import { api } from '../client';

export interface ConsumptionRecord {
  id: string;
  companyId: string;
  itemId: string;
  item: {
    id: string;
    name: string;
    sku: string;
    category: string;
    unit: string;
  };
  locationId: string;
  location: {
    id: string;
    name: string;
    code: string;
  };
  workOrderId: string | null;
  workOrder: {
    id: string;
    workOrderNumber: string;
    title: string;
  } | null;
  quantity: number;
  unit: string;
  consumedAt: string;
  consumedById: string;
  consumedBy: {
    id: string;
    fullName: string;
    email: string;
  };
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConsumptionDto {
  itemId: string;
  locationId: string;
  quantity: number;
  unit: string;
  workOrderId?: string;
  notes?: string;
  consumedAt?: string;
  batchIds?: string[];
}

export interface QueryConsumptionParams {
  itemId?: string;
  locationId?: string;
  workOrderId?: string;
  consumedById?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export type ConsumptionGroupBy = 'ITEM' | 'LOCATION' | 'DATE' | 'WORK_ORDER';

export interface ConsumptionSummaryParams {
  groupBy?: ConsumptionGroupBy;
  startDate?: string;
  endDate?: string;
  locationId?: string;
  itemId?: string;
}

export interface ConsumptionTrendsParams {
  itemId?: string;
  locationId?: string;
  interval?: 'daily' | 'weekly' | 'monthly';
  periods?: number;
}

export interface ConsumptionDashboard {
  summary: {
    today: { quantity: number; count: number };
    week: { quantity: number; count: number };
    month: { quantity: number; count: number };
  };
  topItems: {
    itemId: string;
    name: string;
    sku: string;
    unit: string;
    quantity: number;
  }[];
  topLocations: {
    locationId: string;
    name: string;
    code: string;
    quantity: number;
  }[];
}

export interface ConsumptionSummaryItem {
  itemId?: string;
  itemName?: string;
  itemSku?: string;
  unit?: string;
  locationId?: string;
  locationName?: string;
  locationCode?: string;
  workOrderId?: string;
  workOrderNumber?: string;
  workOrderTitle?: string;
  date?: string;
  totalQuantity: number;
  recordCount: number;
}

export interface ConsumptionTrendItem {
  period: string;
  quantity: number;
  count: number;
}

interface ConsumptionPaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const consumptionApi = {
  getAll: (params?: QueryConsumptionParams) =>
    api.get<ConsumptionPaginatedResponse<ConsumptionRecord>>('/api/consumption', { params }),

  getById: (id: string) => api.get<ConsumptionRecord>(`/api/consumption/${id}`),

  getDashboard: () => api.get<ConsumptionDashboard>('/api/consumption/dashboard'),

  getSummary: (params?: ConsumptionSummaryParams) =>
    api.get<ConsumptionSummaryItem[]>('/api/consumption/summary', { params }),

  getTrends: (params?: ConsumptionTrendsParams) =>
    api.get<ConsumptionTrendItem[]>('/api/consumption/trends', { params }),

  record: (data: CreateConsumptionDto) =>
    api.post<ConsumptionRecord>('/api/consumption', data),

  recordBulk: (items: CreateConsumptionDto[]) =>
    api.post<{
      success: number;
      failed: number;
      results: ConsumptionRecord[];
      errors: { item: CreateConsumptionDto; error: string }[];
    }>('/api/consumption/bulk', { items }),

  delete: (id: string) => api.delete<{ success: boolean }>(`/api/consumption/${id}`),
};
